import { FlightPlanRouteModel, RouteWaypointModel } from '@fe/core';
import { ChartFeature, ChartGeometry, ChartPointGeometry, ChartVectorDataSourceSpecification } from '../chart/chart-props';
import { DistanceElement, RouteAltitudeSource } from '../types';
const DEFAULT_ALTITUDE = 370;

type Item = [number, number];

export function decideRouteAltitudeSource(plannedRoute: FlightPlanRouteModel | null): RouteAltitudeSource {
    if (!plannedRoute) {
        return RouteAltitudeSource.default;
    }

    const { cruisingAltitude, waypoints } = plannedRoute;
    if (cruisingAltitude != null) {
        return RouteAltitudeSource.cruisingAltitude;
    }

    if (waypoints.every((x) => x.altitude == null)) {
        return RouteAltitudeSource.default;
    }

    return RouteAltitudeSource.waypoints;
}

export function plannedToDataSet(
    route: DistanceElement<RouteWaypointModel>[],
    altitudeSource: RouteAltitudeSource,
    cruisingAltitude: number | null
): ChartVectorDataSourceSpecification<ChartGeometry, RouteWaypointModel> {
    const altitudesInFL = areAltitudesInFL(route);
    const dataItems: Item[] = [];
    const pointFeatures: ChartFeature<ChartPointGeometry, RouteWaypointModel>[] = [];
    let i = 0;
    for (const [distance, point] of route) {
        const altitude = getAltitudeFunc(altitudeSource, cruisingAltitude, altitudesInFL)(point);
        if (altitude === null) {
            continue;
        }

        dataItems.push([distance, altitude]);
        pointFeatures.push({
            geometry: {
                type: 'Point',
                coordinates: [distance, altitude]
            },
            id: 'pp-' + (i++),
            properties: point
        });
    }

    addSlopes(dataItems, altitudeSource);
    return {
        type: 'vector',
        features: [
            {
                geometry: {
                    type: 'Line',
                    coordinates: dataItems.map(([distance, altitude]) => [distance, altitude])
                }
            },
            ...pointFeatures
        ]
    };
}

// Sets altitudes for the given planned route. (planned and ATC)
// If cruisingAltitude is set, all waypoints will have cruisingAltitude
// If all waypoints have no altitude, all waypoints will have defaultAltitude
// Otherwise, waypoints will have their altitudes
function getAltitudeFunc(
    altitudeSource: RouteAltitudeSource,
    cruisingAltitude: number | null,
    altitudeInFL: boolean
): (p: RouteWaypointModel) => number | null {
    switch (altitudeSource) {
        case RouteAltitudeSource.cruisingAltitude:
            return () => cruisingAltitude ?? DEFAULT_ALTITUDE;
        case RouteAltitudeSource.default:
            return () => DEFAULT_ALTITUDE;
        case RouteAltitudeSource.waypoints:
            return (p) => (p.altitude != null ? (altitudeInFL ? p.altitude : p.altitude / 100) : null);
    }
}

// Adds slopes to planned or ATC routes. Used when default or cruising altitudes are set
function addSlopes(data: Item[], altitudeSource: RouteAltitudeSource): void {
    // Duplicate first and last item in order to preserve number of data elements needed for impacts
    if (altitudeSource === RouteAltitudeSource.waypoints) {
        insertDummySlopeElements(data);
        return;
    }

    const ascendSlope = 350 / 100; // 350FL per 100NM
    const descentSlope = -350 / 120; // 350FL per 120NM
    insertSlopeElement(data, 0, ascendSlope);
    insertSlopeElement(data, data.length - 1, descentSlope);
}

// BaseElement = Origin or Destination (where altitude should be 0)
function insertSlopeElement(data: Item[], index: number, slope: number): void {
    if (data.length < 2) {
        return;
    }
    const [x, y] = data[index];
    const altitude = y;
    const intercept = -slope * x;
    const levelOffDistance = (altitude - intercept) / slope;
    const item: Item = [levelOffDistance, altitude];

    if (slope > 0) {
        let ndx = 0;
        while (data[ndx][0] < levelOffDistance) {
            data[ndx][1] = slope * data[ndx][0] + intercept;
            ndx++;
        }
        data.splice(ndx, 0, item);
    } else {
        let ndx = data.length - 1;
        while (data[ndx][0] > levelOffDistance) {
            data[ndx][1] = slope * data[ndx][0] + intercept;
            ndx--;
        }
        data.splice(ndx + 1, 0, item);
    }
}

// Puts additional first and last element.
function insertDummySlopeElements(data: Item[]) {
    const first = data[0];
    const last = data[data.length - 1];
    data.splice(0, 0, first);
    data.splice(data.length, 0, last);
}

// Detect if altitudes are in 100s of feet (FL)
function areAltitudesInFL(route: DistanceElement<RouteWaypointModel>[]): boolean {
    // If any altitude is greater than 1000, we assume that all altitudes are in feet
    for (const [, p] of route) {
        if ((p.altitude ?? 0) > 1000) {
            return false;
        }
    }
    return true;
}
